import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createHomeAddress,
  fetchHomeAddresses,
  updateHomeAddress,
} from "@/services/customerService";
import { Helmet } from 'react-helmet-async';
import UserSidebar from "@/components/UserSidebar";
import { ArrowLeft, MapPin, Home, User, Building, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { fetchAddressByPostalCode } from "@/services/postalCodeService";

type Address = {
  id: string;
  name: string;
  phone_number: string;
  postal_code: string;
  address_line1: string;
  address_line2?: string;
};

const HomeAddressPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from;
  const lotteryResultIds = location.state?.lotteryResultIds;

  const [address, setAddress] = useState<Address | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [isLoadingAddress, setIsLoadingAddress] = useState<boolean>(false);

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const fetchedAddresses = await fetchHomeAddresses();
        // 最初の住所のみを取得（1ユーザーにつき1つのみ）
        if (fetchedAddresses.length > 0) {
          const fetchedAddress = fetchedAddresses[0];
          setAddress(fetchedAddress);
          setPhoneNumber(fetchedAddress.phone_number || "");
          setPostalCode(fetchedAddress.postal_code || "");
          setAddressLine1(fetchedAddress.address_line1 || "");
        }
      } catch (err: any) {
        setError("住所の取得に失敗しました。");
      }
    };
    fetchAddress();
  }, []);

  // 郵便番号変更時のハンドラー（住所自動補完）
  const handlePostalCodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPostalCode(value);

    // 7桁入力されたら自動的に住所を取得
    if (value.length === 7 && /^\d{7}$/.test(value)) {
      setIsLoadingAddress(true);
      try {
        const fetchedAddress = await fetchAddressByPostalCode(value);
        if (fetchedAddress) {
          setAddressLine1(fetchedAddress);
        } else {
          toast.error("該当する住所が見つかりませんでした");
        }
      } catch (error: any) {
        toast.error(error.message || "住所の取得に失敗しました");
      } finally {
        setIsLoadingAddress(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const payload = {
      name: e.target["name"].value,
      phone_number: phoneNumber,
      postal_code: postalCode,
      address_line1: addressLine1,
      address_line2: e.target["addressLine2"].value || "",
    };

    try {
      if (address && address.id) {
        // 住所が既に存在する場合は更新
        const updatedAddress = await updateHomeAddress(address.id, payload);
        setAddress(updatedAddress);
        toast.success("住所が更新されました");
      } else {
        // 住所が存在しない場合は新規作成
        const newAddress = await createHomeAddress(payload);
        setAddress(newAddress);
        toast.success("住所が登録されました");
      }
    } catch (err: any) {
      setError("住所の登録/更新に失敗しました。");
    }
  };

  return (
    <>
      <Helmet>
        <title>お届け先住所の編集</title>
        <meta property="og:title" content="お届け先住所の編集" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
          <div className="flex gap-8">
            {/* サイドバー - デスクトップのみ */}
            <div className="hidden lg:block">
              <UserSidebar />
            </div>

            {/* メインコンテンツ */}
            <section className="flex-1">
              {/* ヘッダー */}
              {from === '/products/confirm' && (
                <div className="mb-4">
                  <Link
                    to="/products/confirm"
                    state={{ lotteryResultIds }}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    配送内容の確認に戻る
                  </Link>
                </div>
              )}

              {/* タイトルセクション */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                  <MapPin className="h-5 w-5 text-theme-button" />
                </div>
                <div>
                  <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">
                    お届け先住所の編集
                  </h1>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                    商品の配送先住所を登録してください
                  </p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border-2 border-red-400 rounded-theme">
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-red-600">{error}</p>
                </div>
              )}

              {/* フォームカード */}
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-4 sm:p-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <User className="h-4 w-4" />
                      氏名
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      defaultValue={address?.name || ""}
                      placeholder="山田 太郎"
                      required
                      className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phoneNumber" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <Phone className="h-4 w-4" />
                      電話番号（ハイフンなし）
                    </Label>
                    <Input
                      id="phoneNumber"
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                      placeholder="09012345678"
                      maxLength={11}
                      required
                      className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="postalCode" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <MapPin className="h-4 w-4" />
                      郵便番号（ハイフンなし）
                    </Label>
                    <div className="relative">
                      <Input
                        id="postalCode"
                        type="text"
                        value={postalCode}
                        onChange={handlePostalCodeChange}
                        placeholder="1234567"
                        maxLength={7}
                        required
                        className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                      />
                      {isLoadingAddress && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                        </div>
                      )}
                    </div>
                    {isLoadingAddress && (
                      <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500 mt-1">住所を取得中...</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="addressLine1" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <Home className="h-4 w-4" />
                      住所1
                    </Label>
                    <Input
                      id="addressLine1"
                      type="text"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder="東京都新宿区西新宿1-1-1"
                      required
                      className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="addressLine2" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <Building className="h-4 w-4" />
                      住所2（任意）
                    </Label>
                    <Input
                      id="addressLine2"
                      type="text"
                      defaultValue={address?.address_line2 || ""}
                      placeholder="マンション名や部屋番号"
                      className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full px-4 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all"
                  >
                    {address ? "住所を更新する" : "住所を登録する"}
                  </button>
                </form>
              </div>

              {/* 登録済み住所の表示 */}
              {address && (
                <div className="mt-6 bg-white border-theme rounded-theme-lg shadow-theme p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                      <Home className="h-4 w-4 text-theme-button" />
                    </div>
                    <h2 className="[font-weight:var(--theme-font-weight)] text-black">登録済み住所</h2>
                  </div>
                  <div className="p-4 bg-gray-100 border-theme rounded-theme">
                    <p className="[font-weight:var(--theme-font-weight)] text-black text-lg">{address.name}</p>
                    <div className="mt-2 space-y-1">
                      {address.phone_number && (
                        <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700">TEL: {address.phone_number}</p>
                      )}
                      <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700">〒{address.postal_code}</p>
                      <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700">{address.address_line1}</p>
                      {address.address_line2 && (
                        <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-700">{address.address_line2}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomeAddressPage;
